<template>
  <v-app-bar app :density="appBarDensity" :height="appBarHeight" color="background">
    <div v-if="isDashboard" class="dashboard-nav">
      <router-link :to="defaultDashboardPath" class="dashboard-logo-link">
        <img v-if="currentTheme === 'light'" src="@/assets/MSQ-horizontal-transparent-black.png"
          class="dashboard-logo py-1" alt="Mission Squad Logo" />
        <img v-else src="@/assets/MSQ-horizontal-transparent-yellow.png" class="dashboard-logo my-2"
          alt="Mission Squad Logo" />
      </router-link>

      <v-tabs v-model="tab" @update:model-value="tabUpdated" color="primary" bg-color="background"
        class="dashboard-tabs" density="compact" height="40" show-arrows>
        <v-tab v-for="t in visibleTabs" :key="t.value" :value="t.value">{{ t.label }}</v-tab>
      </v-tabs>

      <div class="dashboard-actions">
        <v-tooltip v-if="showGlobalTooltips" location="bottom">
          <template v-slot:activator="{ props: tooltip }">
            <v-btn icon variant="plain" color="primary" density="compact" class="dashboard-action-button"
              @click="openConfigChat" v-bind="tooltip">
              <v-icon>mdi-robot-outline</v-icon>
            </v-btn>
          </template>
          <span>Configuration Assistant</span>
        </v-tooltip>
        <v-btn v-else icon variant="plain" color="primary" density="compact" class="dashboard-action-button"
          @click="openConfigChat">
          <v-icon>mdi-robot-outline</v-icon>
        </v-btn>

        <v-menu v-model="dashboardMenuOpen" location="bottom end" :close-on-content-click="false" eager>
          <template v-slot:activator="{ props }">
            <v-btn icon v-bind="props" variant="plain" color="primary" density="compact" class="dashboard-action-button">
              <div class="hamburger" :class="{ 'is-active': dashboardMenuOpen }">
                <span class="hamburger-line" />
                <span class="hamburger-line" />
                <span class="hamburger-line" />
              </div>
            </v-btn>
          </template>
          <v-list density="compact" min-width="280" class="nav-menu-list">
            <v-list-item :prepend-icon="currentTheme === 'light' ? 'mdi-weather-sunny' : 'mdi-weather-night'"
              :title="currentTheme === 'light' ? 'Dark Mode' : 'Light Mode'"
              @click="selectTheme(currentTheme === 'light' ? 'dark' : 'light')">
              <template v-slot:append>
                <v-switch :model-value="currentTheme === 'light'" @update:model-value="selectTheme($event ? 'light' : 'dark')"
                  color="primary" inset flat hide-details density="compact" class="nav-menu-switch" @click.stop />
              </template>
            </v-list-item>
            <v-list-item prepend-icon="mdi-information-outline"
              :title="showGlobalTooltips ? 'Tooltips (on)' : 'Tooltips (off)'"
              @click="toggleGlobalTooltips">
              <template v-slot:append>
                <v-switch :model-value="showGlobalTooltips" @update:model-value="toggleGlobalTooltips"
                  color="primary" inset flat hide-details density="compact" class="nav-menu-switch" @click.stop />
              </template>
            </v-list-item>

            <v-list-item
              :prepend-icon="userMode === 'simple' ? 'mdi-tune-variant' : 'mdi-rocket-launch-outline'"
              :title="userMode === 'simple' ? 'Switch to Advanced' : 'Switch to Simple'"
              @click="toggleUserMode" />

            <v-divider class="my-1" />

            <v-list-item @click="openAgentWizard" prepend-icon="mdi-wizard-hat" title="Agent Wizard" />
            <v-list-item @click="openOptions" prepend-icon="mdi-cog-outline" title="Options" />

            <v-divider class="my-1" />

            <v-list-item v-if="authenticated" @click="navigateToDocs" prepend-icon="mdi-help-circle-outline" title="Documentation" />
            <v-list-item v-if="authenticated && stripeEnabled" @click="navigateToSubscription" prepend-icon="mdi-credit-card-outline" title="Subscription" />
            <v-list-item v-if="isAdmin" @click="navigateToAdmin" prepend-icon="mdi-shield-crown" title="Admin" />

            <v-divider class="my-1" />

            <v-list-item @click="navigateToAccount" prepend-icon="mdi-account-outline">
              <v-list-item-title>Account ({{ username }})</v-list-item-title>
            </v-list-item>
            <v-list-item @click="signOut" prepend-icon="mdi-logout" title="Sign Out" />
          </v-list>
        </v-menu>
      </div>
    </div>

    <v-row v-else :class="{ 'ma-0': isLanding }">
      <v-col cols="12" :class="{ 'pa-0': isLanding }">
        <v-row v-if="!isLanding">
          <v-col cols="12" class="col-center">
            <router-link to="/" :class="{ 'logo-link-text': currentTheme === 'light' }">
              <img v-if="currentTheme === 'light'" src="@/assets/MSQ-horizontal-transparent-black.png"
                class="max-width-100 max-height-75px" alt="Mission Squad Logo" />
              <img v-else src="@/assets/MSQ-horizontal-transparent-yellow.png" class="max-width-100 max-height-75px"
                alt="Mission Squad Logo" />
            </router-link>
          </v-col>
        </v-row>
        <v-row :class="isLanding ? 'ma-0' : 'mt-0'">
          <v-col v-if="isLanding" class="background d-flex align-center py-0 pl-4">
            <router-link to="/" class="landing-logo-link">
              <img v-if="currentTheme === 'light'" src="@/assets/MSQ-horizontal-transparent-black.png"
                class="landing-nav-logo" alt="Mission Squad Logo" />
              <img v-else src="@/assets/MSQ-horizontal-transparent-yellow.png" class="landing-nav-logo"
                alt="Mission Squad Logo" />
            </router-link>
          </v-col>
          <v-col v-if="!isLanding && shouldHide(['xs','sm','md'])" xxl="2" xl="2" lg="2" md="1" sm="1" xs="1" class="background">

          </v-col>
          <v-col v-if="!isLanding" xxl="8" xl="8" lg="8" md="10" sm="9" xs="12" class="background banner-text px-4">
          </v-col>
          <v-col xxl="2" xl="2" lg="2" md="2" sm="3" xs="3"
            class="background pr-4 nav-links d-flex align-center justify-end py-0">
            <router-link v-if="!authenticated" to="/sign-in" class="mr-2 sign-in-dashboard-link">Sign in</router-link>
            <router-link v-if="authenticated" :to="defaultDashboardPath"
              class="mr-2 sign-in-dashboard-link">Dashboard</router-link>
            <!-- Hamburger menu for non-dashboard pages -->
            <v-menu v-model="pageMenuOpen" location="bottom end" :close-on-content-click="false" eager>
              <template v-slot:activator="{ props }">
                <v-btn icon v-bind="props" variant="plain" color="primary">
                  <div class="hamburger" :class="{ 'is-active': pageMenuOpen }">
                    <span class="hamburger-line" />
                    <span class="hamburger-line" />
                    <span class="hamburger-line" />
                  </div>
                </v-btn>
              </template>
              <v-list density="compact" min-width="280" class="nav-menu-list">
                <v-list-item :prepend-icon="currentTheme === 'light' ? 'mdi-weather-sunny' : 'mdi-weather-night'"
                  :title="currentTheme === 'light' ? 'Dark Mode' : 'Light Mode'"
                  @click="selectTheme(currentTheme === 'light' ? 'dark' : 'light')">
                  <template v-slot:append>
                    <v-switch :model-value="currentTheme === 'light'" @update:model-value="selectTheme($event ? 'light' : 'dark')"
                      color="primary" inset flat hide-details density="compact" class="nav-menu-switch" @click.stop />
                  </template>
                </v-list-item>
                <v-divider v-if="authenticated" class="my-1" />
                <v-list-item v-if="authenticated" @click="navigateToDocs" prepend-icon="mdi-help-circle-outline" title="Documentation" />
                <v-list-item v-if="authenticated && stripeEnabled" @click="navigateToSubscription" prepend-icon="mdi-credit-card-outline" title="Subscription" />
              </v-list>
            </v-menu>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <OptionsDialog />
  </v-app-bar>
</template>
<script lang="ts">
import { Options, mixins } from 'vue-class-component'
import { mapGetters } from 'vuex'
import AnalyticsMixin from '@/mixins/AnalyticsMixin'
import OptionsDialog from '@/components/OptionsDialog.vue'
import { useTheme } from 'vuetify' 
import {
  ADVANCED_DASHBOARD_TABS,
  SIMPLE_DASHBOARD_TABS,
  defaultDashboardPath,
  getDashboardTabPath,
  toDashboardTabValue,
  type UserMode
} from '@/router/dashboardTabs'

const DASHBOARD_TAB_LABELS: Record<string, string> = {
  providers: 'Providers',
  models: 'Models',
  agents: 'Agents',
  assistants: 'Assistants',
  automations: 'Automations',
  workflows: 'Workflows',
  factories: 'Factories',
  pages: 'Pages',
  schedules: 'Schedules',
  embeddings: 'Embeddings',
  chat: 'Chat',
  mcp: 'MCP'
}

@Options({
  components: {
    OptionsDialog
  },
  computed: {
    ...mapGetters(['username', 'authenticated', 'selectedTab', 'showGlobalTooltips', 'showLog', 'currentTheme', 'isAdmin', 'userMode']),
    breakpoint() {
      return this.$vuetify.display.name
    }
  },
  watch: {
    breakpoint(value: any) {
      this.hide()
    },
    '$route'() {
      this.dashboardMenuOpen = false
      this.pageMenuOpen = false
    }
  }
})
export default class NavComponent extends mixins(AnalyticsMixin) {
  public breakpoint!: string
  public username!: string
  public authenticated!: boolean
  public isAdmin!: boolean
  public showGlobalTooltips!: boolean
  public showLog!: boolean
  public currentTheme!: string
  public userMode!: UserMode | null

  // Dashboard tabs the user can see, in mode-specific order. Simple mode hides the technical surfaces.
  get visibleTabs(): { value: string; label: string }[] {
    const simple = this.userMode === 'simple'
    const order = simple ? SIMPLE_DASHBOARD_TABS : ADVANCED_DASHBOARD_TABS
    return order.map((value) => ({
      value,
      // In simple mode the MCP screen is presented as "Connections".
      label: simple && value === 'mcp' ? 'Connections' : DASHBOARD_TAB_LABELS[value]
    }))
  }

  toggleUserMode() {
    const next: UserMode = this.userMode === 'simple' ? 'advanced' : 'simple'
    this.trackButtonClick('toggle_user_mode', 'click', { mode: next })
    this.$store.dispatch('setUserMode', next)
  }
  theme = useTheme()
  dashboardMenuOpen = false
  pageMenuOpen = false
  defaultDashboardPath = defaultDashboardPath
  
  // Computed property for tab
  get tab() {
    return this.$store.getters.selectedTab
  }
  
  set tab(value: string) {
    this.navigateToDashboardTab(value)
  }

  get appBarDensity() {
    if (this.isDashboard) {
      return 'compact'
    }
    return this.$route.path === '/' ? 'default' : 'prominent'
  }

  get appBarHeight() {
    return this.isDashboard ? 76 : undefined
  }

  get isDashboard() {
    return this.$route.path.startsWith('/dashboard')
  }

  get isLanding() {
    return this.$route.path === '/'
  }

  get isAccount() {
    return this.$route.path.includes('/account')
  }

  get path() {
    return this.$route.path
  }

  get stripeEnabled() {
    return Boolean(this.$store.getters['subscription/stripeEnabled'])
  }

  async created() {
    await this.refreshStripeAvailability()
  }

  async signOut() {
    this.trackButtonClick('sign_out', 'click');
    await this.$store.dispatch('signOut')
    this.$router.push('/sign-in')
  }

  get logVisible() {
    return this.showLog ?? false
  }

  set logVisible(value: boolean) {
    this.$store.dispatch('toggleLog')
  }

  toggleLog() {
    this.trackButtonClick('toggle_event_log', 'click', { visible: !this.logVisible });
    this.$store.dispatch('toggleLog')
  }

  shouldHide(cols: string[]) {
    const bp = this.breakpoint ?? ''
    if (cols.includes(bp)) {
      return false
    }
    return true
  }
  
  shouldShow(cols: string[]) {
    const bp = this.breakpoint ?? ''
    if (cols.includes(bp)) {
      return true
    }
    return false
  }

  hide(): boolean {
    const bp = this.breakpoint ?? ''
    if (bp === 'xs' || bp === 'sm' || bp === 'md') {
      return true
    }
    return false
  }

  openOptions() {
    this.trackButtonClick('open_options_dialog', 'click');
    this.$store.dispatch('toggleOptions', true)
  }

  openConfigChat() {
    this.$store.dispatch('toggleConfigChat', true)
  }

  openAgentWizard() {
    this.$store.dispatch('toggleAgentWizard', true);
  }

  navigateToDashboardTab(value: string) {
    const tabValue = toDashboardTabValue(value)
    if (tabValue == null) {
      return
    }
    this.trackButtonClick('select_main_tab', 'navigate', { tab_name: tabValue });
    if (this.$store.getters.selectedTab !== tabValue) {
      this.$store.dispatch('setTab', tabValue)
    }
    const path = getDashboardTabPath(tabValue)
    if (this.$route.path !== path) {
      this.$router.push(path)
    }
  }

  async refreshStripeAvailability() {
    await this.$store.dispatch('subscription/ensureAvailabilityChecked')
  }
  
  tabUpdated() {
    // Any additional logic needed when tab is updated
  }
  
  toggleGlobalTooltips() {
    this.trackButtonClick('toggle_global_tooltips', 'click', { enabled: !this.showGlobalTooltips });
    this.$store.dispatch('toggleGlobalTooltips')
  }

  // Method to set the theme - dispatches to Vuex store
  selectTheme(themeName: string) {
    this.trackFeatureUsage('ui', 'select_theme', { theme_name: themeName });
    this.$store.dispatch('setTheme', themeName)
  }
  
  navigateToAccount() {
    this.trackButtonClick('navigate_to_account', 'click');
    this.$router.push('/account');
  }

  navigateToAdmin() {
    this.trackButtonClick('navigate_to_admin', 'click');
    this.$router.push('/admin');
  }

  navigateToSubscription() {
    this.trackButtonClick('navigate_to_subscription', 'click')
    this.$router.push('/account/subscription')
  }

  openDiscord() {
    this.trackButtonClick('open_discord', 'navigate', 'https://discord.gg/GbGnkUec3e');
    window.open('https://discord.gg/GbGnkUec3e', '_blank', 'noopener');
  }

  navigateToDocs() {
    this.trackButtonClick('open_docs', 'navigate', 'https://docs.missionsquad.ai');
    window.open('https://docs.missionsquad.ai', '_blank', 'noopener');
  }
}
</script>
<style lang="scss" scoped>
.col-center {
  text-align: center;
}

.banner {
  background-color: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-on-surface)); // Added for text contrast on banner
}

.banner-text {
  text-align: center;
}

.nav-links {
  text-align: right;
  text-transform: capitalize;
  a { // Ensure links within .nav-links also use on-primary
    color: rgb(var(--v-theme-on-surface)) !important; 
    &:visited {
      color: rgb(var(--v-theme-on-surface)) !important;
    }
    &.router-link-exact-active {
      // Consider a different color for active link on primary background if needed
      // For now, it will inherit from the general 'a' rule above
    }

    &.sign-in-dashboard-link {
      margin-top: 2px;
      font-weight: bold;
      text-decoration: none;
      color: rgb(var(--v-theme-primary)) !important; // Primary color for sign-in/dashboard links

      &:visited {
        color: rgb(var(--v-theme-primary)) !important;
      }
    }
  }
}
.link-padding {
  padding-top: 3px;
}

.dashboard-nav {
  align-items: center;
  display: flex;
  gap: 8px;
  min-width: 0;
  padding: 0 8px;
  width: 100%;
}

.dashboard-logo-link {
  align-items: center;
  display: inline-flex;
  flex: 0 0 auto;
  min-height: 67px;
  justify-content: center;
  text-decoration: none;
}

.dashboard-logo {
  display: block;
  max-height: 51px;
  max-width: 231px;
  object-fit: contain;
}

.landing-logo-link {
  display: inline-flex;
  align-items: center;
  text-decoration: none;
}

.landing-nav-logo {
  display: block;
  height: 56px;
  max-width: 100%;
  object-fit: contain;
}

@media (max-width: 600px) {
  .landing-nav-logo {
    height: 44px;
  }
}

.dashboard-tabs {
  flex: 1 1 auto;
  min-width: 0;
}

.dashboard-actions {
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  gap: 2px;
}

.dashboard-action-button {
  height: 36px;
  width: 36px;
}

.nav-menu-list {
  padding-bottom: 8px;
  padding-top: 8px;
}

:deep(.dashboard-tabs .v-slide-group__container) {
  overflow-x: auto;
}

:deep(.dashboard-tabs .v-tab) {
  min-width: max-content;
  padding: 0 12px;
}


nav { // This style block seems to be for a different 'nav' element, not the v-app-bar
  padding: 30px;
  // background-color: rgb(var(--v-theme-primary)); // If this nav is also primary
  // color: rgb(var(--v-theme-on-primary)); // Text color for this nav

  a {
    font-weight: bold;
    color: rgb(var(--v-theme-on-surface)); // Default link color on a generic surface

    &:visited {
      color: rgb(var(--v-theme-on-surface));
    }

    &.router-link-exact-active {
      color: rgb(var(--v-theme-success)); // Using success for active link highlight
    }
  }
}

.text-logo {
  font-family: 'Novecento Sans Wide', sans-serif;
  font-size: 40px; /* Adjust as needed to fit well, considering max-height-75px of the image */
  line-height: 1.2; /* Adjust for vertical centering within the 75px space */
  font-weight: normal; /* Or the specific weight of your font file */
  color: rgb(var(--v-theme-primary)); /* Uses the primary color of the current theme */
  display: inline-block; /* Allows for better box model control */
  padding-top: 10px; /* Adjust padding to help with vertical alignment */
  padding-bottom: 10px; /* Adjust padding */
  vertical-align: middle;
}

.logo-link-text { /* Styles for the router-link when it contains the text logo */
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  height: 75px; /* Match the image's max-height constraint */
}

.nav-menu-switch {
  transform: scale(0.70);
  transform-origin: center right;
}

.hamburger {
  width: 22px;
  height: 16px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  cursor: pointer;
}

.hamburger-line {
  display: block;
  width: 100%;
  height: 2px;
  background-color: rgb(var(--v-theme-primary));
  border-radius: 1px;
  transition: transform 0.3s ease, opacity 0.3s ease;
  transform-origin: center;
}

.hamburger.is-active .hamburger-line:nth-child(1) {
  transform: translateY(7px) rotate(-45deg);
}

.hamburger.is-active .hamburger-line:nth-child(2) {
  opacity: 0;
}

.hamburger.is-active .hamburger-line:nth-child(3) {
  transform: translateY(-7px) rotate(45deg);
}
</style>
